// Builds metadata for the Guru Cloud AI feedback widget.
// The widget reads `window.GuruWidgetMetadata` when a submission is created.
//
// Basic user + page metadata is set synchronously. When an emailId or orderId
// is detected in the URL, the details (subject, customer name, etc.) are
// fetched asynchronously and merged into the metadata before the user submits.

import type { User } from '../types/auth';
import { portalService } from '../services/portal';
import { portalOrderService } from '../services/portalOrders';

/** Metadata shape sent with every Guru widget submission. */
export interface GuruWidgetMetadata {
  // User identity
  user_id: string;
  user_email: string;
  user_name: string;
  user_role: string;
  user_status: string;
  salesperson_id?: string;
  assigned_accounts?: string;
  scopes: string;
  // Persona membership, comma-separated (e.g. "tech-support-copilot").
  // The Guru widget explodes comma-joined segment keys into one membership
  // hash per value, so notifications/digests can target a single persona.
  persona: string;
  // Admin "View as": the account whose portal the admin is looking at. The
  // user_* fields above always name the real person at the keyboard.
  viewed_as_user_id?: string;
  viewed_as_user_email?: string;
  viewed_as_user_name?: string;
  viewed_as_user_role?: string;
  // App context
  app_section: 'admin' | 'portal';
  page_url: string;
  page_path: string;
  page_title: string;
  timestamp: string;
  // Contextual IDs from URL
  order_id?: string;
  email_id?: string;
  account_id?: string;
  // Viewed-entity context, registered by viewing components (modals, panels)
  // whose entity ID lives in component state and never reaches the URL.
  // Comma-joined registration keys of the surfaces currently in view.
  view_context?: string;
  thread_id?: string;
  thread_subject?: string;
  mail_folder?: string;
  mail_account?: string;
  composer_mode?: string;
  // AI conversation context (copilot / agent chat surfaces)
  copilot?: string;
  conversation_id?: string;
  conversation_title?: string;
  chat_scope?: string;
  agent_name?: string;
  model_id?: string;
  // Enriched email details (async)
  email_subject?: string;
  email_from_name?: string;
  email_from_address?: string;
  email_customer_name?: string;
  // Enriched order details (async)
  order_customer_name?: string;
  order_customer_no?: string;
  order_status?: string;
  order_total?: string;
  order_po_number?: string;
  order_salesperson?: string;
  // Browser / environment
  screen_resolution: string;
  viewport_size: string;
  user_agent: string;
  timezone: string;
  locale: string;
}

declare global {
  interface Window {
    GuruWidgetMetadata?: GuruWidgetMetadata;
  }
}

// Track the current enrichment request so stale fetches don't overwrite fresh metadata.
let _enrichmentVersion = 0;

// ─── Viewed-entity context registry ─────────────────────────────────────────
//
// Components that display an entity whose ID lives in component state (a
// detail modal, the tech-support mail panel, …) register what's in view here.
// The registered fields are overlaid on the base (user + URL) metadata every
// time window.GuruWidgetMetadata is published, so widget submissions carry
// the viewed email/order/thread even when the URL says nothing about it.
//
// Later registrations win over earlier ones (a modal opened on top of a page
// is more specific), and every context wins over URL-derived IDs.

/** Fields a viewing component may register. Identity/app fields are excluded on purpose. */
export type GuruWidgetViewContext = Partial<
  Pick<
    GuruWidgetMetadata,
    | 'email_id'
    | 'email_subject'
    | 'email_from_name'
    | 'email_from_address'
    | 'email_customer_name'
    | 'order_id'
    | 'order_customer_name'
    | 'order_customer_no'
    | 'order_status'
    | 'order_total'
    | 'order_po_number'
    | 'order_salesperson'
    | 'account_id'
    | 'thread_id'
    | 'thread_subject'
    | 'mail_folder'
    | 'mail_account'
    | 'composer_mode'
    | 'copilot'
    | 'conversation_id'
    | 'conversation_title'
    | 'chat_scope'
    | 'agent_name'
    | 'model_id'
  >
>;

// Base metadata (user + page + URL-derived + async enrichments), kept so the
// overlay can be recomputed from scratch when view contexts come and go.
let _baseMetadata: GuruWidgetMetadata | undefined;
const _viewContexts = new Map<string, GuruWidgetViewContext>();

/** Recompute window.GuruWidgetMetadata = base + registered view contexts. */
function publishMetadata(): void {
  if (!_baseMetadata) {
    window.GuruWidgetMetadata = undefined;
    return;
  }
  const merged: GuruWidgetMetadata = { ..._baseMetadata };
  for (const ctx of _viewContexts.values()) {
    for (const [key, value] of Object.entries(ctx) as [keyof GuruWidgetViewContext, string | undefined][]) {
      if (value !== undefined) {
        merged[key] = value;
      }
    }
  }
  if (_viewContexts.size > 0) {
    // Keys may carry a per-instance suffix ("email_view#:r1:") — strip it and
    // dedupe for the human-readable label.
    const labels = [...new Set([..._viewContexts.keys()].map((k) => k.split('#')[0]))];
    merged.view_context = labels.join(', ');
  }
  window.GuruWidgetMetadata = merged;
}

/**
 * Register (or replace) the entity a component is currently displaying.
 * Pass null to clear (e.g. modal closed). Registrations survive route-level
 * metadata syncs; the owning component clears them on unmount.
 */
export function setGuruWidgetViewContext(key: string, ctx: GuruWidgetViewContext | null): void {
  if (ctx === null) {
    if (!_viewContexts.delete(key)) return;
  } else {
    // Delete first so a re-registration moves to the end (most recent wins).
    _viewContexts.delete(key);
    _viewContexts.set(key, ctx);
  }
  publishMetadata();
}

/** Clear a view-context registration. */
export function clearGuruWidgetViewContext(key: string): void {
  setGuruWidgetViewContext(key, null);
}

// Co-pilot scope → persona name. Persona is derived from the USER (not the
// current page) so it stays stable across navigation.
const PERSONA_SCOPES: Record<string, string> = {
  'techsupport:copilot': 'tech-support-copilot',
  'quality:copilot': 'quality-copilot',
  // The read-only Quality view (kanban 808dabf6): the usage graph tells a
  // viewer's look at the desk from the desk's own work.
  'quality:viewer': 'quality-viewer',
  'analyst:copilot': 'analyst-copilot',
};

/**
 * Derive persona membership from scopes, falling back to the user's role.
 * Also the `user_role` the user-action recorder reports (same role-in-product
 * the widget segments on), see hooks/useGuruRecorder.ts.
 */
export function derivePersonas(user: User): string {
  // Tolerate a user payload missing `scopes` / `role`: this runs on every
  // route change, so a throw here would blank the SPA (kanban 90f3b9d8).
  const personas = (Array.isArray(user.scopes) ? user.scopes : [])
    .map((scope) => PERSONA_SCOPES[scope])
    .filter((p): p is string => Boolean(p));
  if (personas.length === 0) {
    personas.push((user.role ?? 'user').toLowerCase());
  }
  return personas.join(', ');
}

/** Extract contextual IDs from a URL path + search string. */
function extractContextIds(pathname: string, search: string): Pick<GuruWidgetMetadata, 'order_id' | 'email_id' | 'account_id'> {
  const ids: Pick<GuruWidgetMetadata, 'order_id' | 'email_id' | 'account_id'> = {};
  const params = new URLSearchParams(search);

  // URL patterns: /portal/orders/:id, /orders/:id, etc.
  const orderMatch = pathname.match(/\/orders\/([^/]+)/);
  if (orderMatch) ids.order_id = orderMatch[1];

  // Query params: ?orderId=..., ?emailId=..., ?accountId=...
  const orderId = params.get('orderId');
  if (orderId) ids.order_id = orderId;

  const emailId = params.get('emailId');
  if (emailId) ids.email_id = emailId;

  const accountMatch = pathname.match(/\/(?:accounts|customers)\/([^/]+)/);
  if (accountMatch) ids.account_id = accountMatch[1];

  const accountId = params.get('accountId');
  if (accountId) ids.account_id = accountId;

  return ids;
}

/** Async-fetch email and order details, then merge into window.GuruWidgetMetadata. */
async function enrichMetadata(emailId: string | undefined, orderId: string | undefined, version: number): Promise<void> {
  if (!emailId && !orderId) return;

  const enrichments: Partial<GuruWidgetMetadata> = {};

  // Fetch in parallel
  const [emailResult, orderResult] = await Promise.allSettled([
    emailId ? portalService.getEmailDetail(emailId) : Promise.resolve(null),
    orderId ? portalOrderService.getOrder(orderId) : Promise.resolve(null),
  ]);

  if (emailResult.status === 'fulfilled' && emailResult.value) {
    const email = emailResult.value;
    enrichments.email_subject = email.subject;
    enrichments.email_from_name = email.from_name;
    enrichments.email_from_address = email.email;
    if (email.customer?.name) {
      enrichments.email_customer_name = email.customer.name;
    }
  }

  if (orderResult.status === 'fulfilled' && orderResult.value) {
    const order = orderResult.value.order;
    enrichments.order_customer_name = order.customer_name;
    enrichments.order_customer_no = order.customer_no;
    enrichments.order_status = order.status;
    enrichments.order_total = order.total ?? undefined;
    enrichments.order_po_number = order.po_number ?? undefined;
    enrichments.order_salesperson = order.salesperson_name;
  }

  // Only apply if this is still the current enrichment (user hasn't navigated away)
  if (_enrichmentVersion === version && _baseMetadata) {
    Object.assign(_baseMetadata, enrichments);
    publishMetadata();
  }
}

/**
 * Build metadata and assign it to `window.GuruWidgetMetadata`.
 *
 * `user` is the real person at the keyboard (AuthContext.realUser); pass the
 * impersonated account as `viewedAs` while an admin uses "View as".
 */
export function syncGuruWidgetMetadata(
  user: User | null,
  appSection: 'admin' | 'portal',
  pathname: string,
  search: string,
  viewedAs: User | null = null,
): void {
  // Bump version so any in-flight enrichment is discarded
  const version = ++_enrichmentVersion;

  if (!user) {
    _baseMetadata = undefined;
    publishMetadata();
    return;
  }

  const contextIds = extractContextIds(pathname, search);

  _baseMetadata = {
    // User identity
    user_id: user.id,
    user_email: user.email,
    user_name: user.name,
    user_role: user.role,
    user_status: user.status,
    salesperson_id: user.salesperson_id ?? undefined,
    assigned_accounts: user.assigned_accounts?.join(', ') || undefined,
    scopes: (Array.isArray(user.scopes) ? user.scopes : []).join(', '),
    persona: derivePersonas(user),
    ...(viewedAs && viewedAs.id !== user.id
      ? {
          viewed_as_user_id: viewedAs.id,
          viewed_as_user_email: viewedAs.email,
          viewed_as_user_name: viewedAs.name,
          viewed_as_user_role: viewedAs.role,
        }
      : {}),
    // App context
    app_section: appSection,
    page_url: window.location.href,
    page_path: pathname,
    page_title: document.title,
    timestamp: new Date().toISOString(),
    // Contextual IDs from URL
    ...contextIds,
    // Browser / environment
    screen_resolution: `${window.screen.width}x${window.screen.height}`,
    viewport_size: `${window.innerWidth}x${window.innerHeight}`,
    user_agent: navigator.userAgent,
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    locale: navigator.language,
  };
  publishMetadata();

  // Fire-and-forget: enrich with email/order details
  enrichMetadata(contextIds.email_id, contextIds.order_id, version).catch(() => {
    // Silently ignore — metadata still has IDs as fallback
  });
}

/**
 * Clear the base metadata (widget embed unmounted). View-context
 * registrations are kept — their owning components clear them on unmount —
 * but nothing is published without a base identity.
 */
export function clearGuruWidgetMetadata(): void {
  _enrichmentVersion++;
  _baseMetadata = undefined;
  publishMetadata();
}

/** Exposed for testing only. */
export function _resetEnrichmentVersion(): void {
  _enrichmentVersion = 0;
  _baseMetadata = undefined;
  _viewContexts.clear();
  window.GuruWidgetMetadata = undefined;
}
